import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { Info } from 'react-feather'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { API_PATH_INTERNAL } from '@/lib/constants'
import {
  EMAIL_ADDRESSES,
  PANEL_INVITE_SOURCE,
  PANEL_STATUS,
  PENDING_PANEL_ACTION,
} from '@/lib/constants'
import {
  faCheckCircle,
  faClock,
  faExclamationTriangle,
} from '@/lib/fontAwesomePro/regular'
import apiService from '@/lib/services/apiService'
import {
  PanelInviteSource,
  PanelStatus,
  PendingPanelAction,
  ReferralArchiveState,
} from '@/types/misc'
import { GetPanelInviteParams } from '@/types/requests/companies'
import {
  CreatePanelFromReferralCodeRequest,
  CreatePanelWithAgreementsRequest,
  CreatePanelWithoutAgreementsRequest,
  GetPanelInvitesParams,
  UpdatePanelInviteRequest,
} from '@/types/requests/panels'
import {
  ApprovedPanelPartnersCollection,
  CreatePanelFromReferralCodeResponse,
  CreatePanelWithAgreementsResponse,
  PanelCollection,
  PanelCollectionWithStats,
  PanelInviteCollection,
  PanelInviteItem,
  PanelItem,
  PanelItemCompany,
  PanelPartnersCollection,
  PanelReferralApprovalsCollection,
  UpdatePanelResponse,
} from '@/types/responses/panels'

export interface GetPanelsParams {
  groupId?: number | undefined
  companyId?: number | undefined
  otherCompanyId?: number
  otherCompanyName?: string
  otherCompanyTypeId?: number
  status: PanelStatus
  page?: number
  pageSize?: number
  referralStats?: boolean
  performanceStats?: boolean
  teamMemberId?: number
  serviceAreaId?: number
  startDate?: string
  endDate?: string
  order?: `order[${GetPanelsOrderParam}]`
  archiveState?: ReferralArchiveState
}

export type GetPanelsOrderParam =
  | 'referralsIn'
  | 'referralsOut'
  | 'income'
  | 'conversion.referralsIn'
  | 'conversion.referralsOut'
  | 'conversion.allReferrals'
  | 'nps'
  | 'response'

export interface GetPanelPartnersParams {
  companyName?: string
  panelStatus?: PanelStatus[]
  // A location filter value, e.g. "country:FR"
  location?: string
  page?: number
  pageSize?: number
}

class PanelService {
  private endpoint = `${API_PATH_INTERNAL}/panels`

  async getPanels(params: GetPanelsParams): Promise<PanelCollection> {
    const response = await apiService.get(this.endpoint, {
      params,
    })

    return response.data
  }

  async getPanelsWithStats(
    params: GetPanelsParams,
  ): Promise<PanelCollectionWithStats> {
    const response = await apiService.get(this.endpoint, {
      params,
    })

    return response.data
  }

  async getPanelReferralApprovals(): Promise<PanelReferralApprovalsCollection> {
    const response = await apiService.get(`${this.endpoint}/referral-approvals`)

    return response.data
  }

  async getPanelPartners(
    params?: GetPanelPartnersParams,
  ): Promise<PanelPartnersCollection> {
    const response = await apiService.get(`${this.endpoint}/partners`, {
      params,
    })

    return response.data
  }

  async getPanelInvites(
    params: GetPanelInvitesParams,
  ): Promise<PanelInviteCollection> {
    const response = await apiService.get(`${this.endpoint}/invites`, {
      params,
    })

    return response.data
  }

  async getPanelInvite(params: GetPanelInviteParams): Promise<PanelInviteItem> {
    const response = await apiService.get(`${this.endpoint}/invite`, { params })

    return response.data
  }

  async getPanel(panelId: number): Promise<PanelItem> {
    const response = await apiService.get(`${this.endpoint}/${panelId}`)

    return response.data
  }

  async acceptPanelInvite(panelId: number): Promise<UpdatePanelResponse> {
    const response = await apiService.patch(`${this.endpoint}/${panelId}`, {
      status: PANEL_STATUS.approved,
    })

    return response.data
  }

  async declinePanelInvite(panelId: number): Promise<unknown> {
    const response = await apiService.patch(`${this.endpoint}/${panelId}`, {
      status: PANEL_STATUS.rejected,
    })

    return response.data
  }

  async createPanelFromInvitePage(
    request: CreatePanelFromReferralCodeRequest,
  ): Promise<CreatePanelFromReferralCodeResponse> {
    const response = await apiService.post(
      `${this.endpoint}/create-from-referral-code`,
      request,
    )
    return response.data
  }

  async createPanelWithAgreements(
    request: CreatePanelWithAgreementsRequest,
  ): Promise<CreatePanelWithAgreementsResponse> {
    const response = await apiService.post(
      `${this.endpoint}/create-with-agreements`,
      request,
    )

    return response.data
  }

  async createPanelWithoutAgreements(
    request: CreatePanelWithoutAgreementsRequest,
  ): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/create-with-agreements`,
      request,
    )

    return response.data
  }

  async updateInternalRelationship(
    panelId: number,
    enableInternalRelationship: boolean,
  ): Promise<UpdatePanelResponse> {
    const response = await apiService.patch(
      `${this.endpoint}/${panelId}/internal-referral`,
      {
        internalRelationship: enableInternalRelationship,
      },
    )

    return response.data
  }

  async updatePanelInvite(request: UpdatePanelInviteRequest) {
    const response = await apiService.post(
      `${this.endpoint}/update-panel-invite`,
      request,
    )

    return response.data
  }

  isValidPanelInviteSource(
    panelInviteSource?: string | null,
  ): panelInviteSource is PanelInviteSource {
    return (
      !!panelInviteSource &&
      Object.keys(PANEL_INVITE_SOURCE).includes(panelInviteSource)
    )
  }

  getPanelStatusStyles(options: GetPanelStatusStylesOptions): PanelStatusStyle {
    const { panelStatus, textType, pendingActions = [] } = options
    const DECLINED_TOOLTIP = `You cannot request this company as a professional relationship as it was previously rejected. Please contact us on ${EMAIL_ADDRESSES.rqHello} if you would like to attempt to add this company to your professional network again`
    const isLongTextType = textType === 'long'

    const isAwaitingResponseFromCurrentCompany = pendingActions.includes(
      PENDING_PANEL_ACTION.respondToInvite,
    )

    const longTextTypeRequestStatus = isAwaitingResponseFromCurrentCompany
      ? 'Respond to invite'
      : 'Professional network relationship request sent'

    const PANEL_STATUS_STYLES: Record<PanelStatus, PanelStatusStyle> = {
      approved: {
        text: isLongTextType ? 'In your professional network' : 'Approved',
        textClass: 'text-success',
        icon: (
          <FontAwesomeIcon
            icon={faCheckCircle}
            style={{ fontSize: '14px', marginRight: '6px' }}
          />
        ),
      },
      requested: {
        text: isLongTextType ? longTextTypeRequestStatus : 'Requested',
        textClass: 'text-warning',
        icon: (
          <FontAwesomeIcon
            icon={
              isAwaitingResponseFromCurrentCompany
                ? faExclamationTriangle
                : faClock
            }
            style={{ fontSize: '14px', marginRight: '6px' }}
          />
        ),
      },
      rejected: {
        text: isLongTextType
          ? 'Professional network relationship request declined'
          : 'Declined',
        textClass: 'text-dark',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={16} style={{ marginRight: '5px' }} />
            </TooltipTrigger>

            <TooltipContent side="bottom">{DECLINED_TOOLTIP}</TooltipContent>
          </Tooltip>
        ),
      },
    }

    return PANEL_STATUS_STYLES[panelStatus]
  }

  async getApprovedPanels(): Promise<ApprovedPanelPartnersCollection> {
    const response = await apiService.get(
      `${API_PATH_INTERNAL}/companies/panels`,
    )

    return response.data
  }

  getOtherPanelCompany(
    panel: PanelItem,
    currentCompanyIri: IRI,
  ): PanelItemCompany {
    return currentCompanyIri === panel.fromCompany['@id']
      ? panel.toCompany
      : panel.fromCompany
  }
}

interface PanelStatusStyle {
  text: string
  textClass: string
  icon: React.ReactNode
}

interface GetPanelStatusStylesOptions {
  panelStatus: PanelStatus
  textType: 'short' | 'long'
  pendingActions?: PendingPanelAction[]
}

const panelService = new PanelService()

export default panelService
